import { SiteAvailabilityControl } from '@see-power-pft-common-js/net-availability/enums/site-availability-control';
import { INetAvailabilityError } from '@see-power-pft-common-js/net-availability/net-availability';
import {
  AssetLimitForNetAvailability,
  DeclaredAvailability,
  SiteLimitForNetAvailability,
} from '@see-power-pft-common-js/net-availability/types';
import { Result } from '@see-power-pft-common-js/types/result';
import {
  clampUtcDateToSameOrNextSp,
  clampUtcDateToSameOrPrevSp,
  IntervalDoesNotAlignToSpError,
} from '@see-power-pft-common-js/utils/sp';
import { AvailabilityChartEntry } from 'pages/Availability/common/AvailabilityChart/types';
import {
  getAssetLevelAvailabilityChartEntries,
  getSiteLevelAvailabilityChartEntries,
} from 'pages/Availability/common/AvailabilityChart/utils/availability-chart-entries';
import {
  AvailabilityChartData,
  AvailabilityChartSeries,
  BaseAssetAvailabilityEntry,
  BaseAvailabilityEntry,
} from 'pages/Availability/types';

const baseConfig = {
  lineWidth: 4,
  clip: false,
  opacity: 1,
  zIndex: 1,
  states: {
    hover: {
      lineWidth: 5,
      opacity: 1,
    },
  },
};

const previewConfig = {
  lineWidth: 4,
  clip: false,
  opacity: 1,
  zIndex: 100,
  states: {
    hover: {
      lineWidth: 6,
      opacity: 0.95,
    },
  },
};

export const powerColors = {
  color: 'rgba(0, 127, 162, 0.2)',
  lineColor: 'rgb(30, 144, 255)',
};

export const previewColors = {
  color: 'rgba(254, 180, 52, 0.3)',
  lineColor: 'rgb(255, 179, 71)',
  hoverColor: 'rgba(254, 180, 52, 0.9)',
  hoverLineColor: 'rgb(255, 179, 71)',
};

const getMinStartTime = (entries: BaseAvailabilityEntry[]): number => {
  return Math.min(
    ...entries.filter((e) => e.start).map((e) => new Date(e.start!).getTime()),
  );
};

const getMaxEndTime = (entries: BaseAvailabilityEntry[]): number | null => {
  const entriesWithEnd = entries.filter((e) => e.end);
  if (entriesWithEnd.length === 0) {
    return null;
  }
  return Math.max(...entriesWithEnd.map((e) => new Date(e.end!).getTime()));
};

export const getPreviewChartTime = (
  previewData: BaseAvailabilityEntry[],
  type: 'start' | 'end',
): string => {
  if (previewData.length === 0) {
    return new Date().toISOString();
  }

  const offset = 60 * 60 * 1000; // 1 hour

  if (type === 'start') {
    const minStart = getMinStartTime(previewData);
    return new Date(minStart - offset).toISOString();
  }

  // For end time, check if any entry is open-ended
  const hasOpenEnded = previewData.some((entry) => entry.isOpenEnded);

  if (hasOpenEnded) {
    const minStart = getMinStartTime(previewData);
    const largeOffset = 60 * 60 * 1000 * 24 * 30; // 30 days
    return new Date(minStart + largeOffset).toISOString();
  }

  // Get the latest end time
  const maxEnd = getMaxEndTime(previewData);
  if (maxEnd === null) {
    const minStart = getMinStartTime(previewData);
    return new Date(minStart + offset).toISOString();
  }

  return new Date(maxEnd + offset).toISOString();
};

export const getPreviewEntriesRange = (
  previewEntries: BaseAvailabilityEntry[],
): { start: string; end: string | null } | null => {
  if (previewEntries.length === 0) {
    return null;
  }

  const minStart = getMinStartTime(previewEntries);

  const hasOpenEnded = previewEntries.some((e) => e.isOpenEnded);
  if (hasOpenEnded) {
    return { start: new Date(minStart).toISOString(), end: null };
  }

  const maxEnd = getMaxEndTime(previewEntries);
  return {
    start: new Date(minStart).toISOString(),
    end: maxEnd ? new Date(maxEnd).toISOString() : null,
  };
};

type NormalizeLimitsInput =
  SiteLimitForNetAvailability | AssetLimitForNetAvailability;

function normalizeLimits<T extends NormalizeLimitsInput>(limits: T[]): T[] {
  return limits.map((limit) => ({
    ...limit,
    start: new Date(limit.start),
  }));
}

function mapToCommonAvailabilityFields(
  entry: BaseAvailabilityEntry,
  chartEnd: Date,
): DeclaredAvailability {
  return {
    start: entry.start ? new Date(entry.start) : new Date(),
    end: entry.end ? new Date(entry.end) : chartEnd,
    isCancelled: entry.isCancelled ?? false,
    maxImportPower: entry.maxImportPower ?? undefined,
    maxExportPower: entry.maxExportPower ?? undefined,
    energyCapacity: entry.energyCapacity ?? undefined,
  };
}

export function buildAvailabilityChartData(params: {
  availabilityControl: SiteAvailabilityControl.SiteLevel;
  availabilityEntries: BaseAvailabilityEntry[];
  availabilityFormOpen: boolean;
  viewDateRange: { start: string; end: string };
  limits: SiteLimitForNetAvailability[];
  assetNames: string[];
  previewEntries: BaseAvailabilityEntry[];
}): AvailabilityChartData | null;

export function buildAvailabilityChartData(params: {
  availabilityControl: SiteAvailabilityControl.AssetLevel;
  availabilityEntries: BaseAssetAvailabilityEntry[];
  availabilityFormOpen: boolean;
  viewDateRange: { start: string; end: string };
  limits: AssetLimitForNetAvailability[];
  assetNames: string[];
  previewEntries: BaseAvailabilityEntry[];
}): AvailabilityChartData | null;

export function buildAvailabilityChartData({
  availabilityControl,
  availabilityEntries,
  availabilityFormOpen,
  viewDateRange,
  limits,
  assetNames,
  previewEntries,
}: {
  availabilityControl: SiteAvailabilityControl;
  availabilityEntries: BaseAvailabilityEntry[] | BaseAssetAvailabilityEntry[];
  availabilityFormOpen: boolean;
  viewDateRange: { start: string; end: string };
  limits: SiteLimitForNetAvailability[] | AssetLimitForNetAvailability[];
  assetNames: string[];
  previewEntries: BaseAvailabilityEntry[];
}): AvailabilityChartData | null {
  const hasValidPreview =
    previewEntries.length > 0 &&
    previewEntries.some(
      (entry) =>
        entry.start !== null && (entry.end === null || entry.start < entry.end),
    );

  if (availabilityFormOpen && !hasValidPreview) {
    return null;
  }

  const chartStart = clampUtcDateToSameOrPrevSp(new Date(viewDateRange.start));
  const chartEnd = clampUtcDateToSameOrNextSp(new Date(viewDateRange.end));
  const timeHorizon = { start: chartStart, end: chartEnd };

  const chartEntries: Result<
    INetAvailabilityError[] | IntervalDoesNotAlignToSpError,
    AvailabilityChartEntry[]
  > = (() => {
    if (availabilityControl === SiteAvailabilityControl.SiteLevel) {
      const chartInput: Parameters<
        typeof getSiteLevelAvailabilityChartEntries
      >[0] = {
        siteLimits: normalizeLimits(limits as SiteLimitForNetAvailability[]),
        siteDeclaredAvailabilities: availabilityEntries.map((entry) =>
          mapToCommonAvailabilityFields(entry, chartEnd),
        ),
        assetNames,
        timeHorizon,
      };
      return getSiteLevelAvailabilityChartEntries(chartInput);
    } else {
      const assetEntries = availabilityEntries as BaseAssetAvailabilityEntry[];
      const chartInput: Parameters<
        typeof getAssetLevelAvailabilityChartEntries
      >[0] = {
        assetNames,
        assetLimits: normalizeLimits(limits as AssetLimitForNetAvailability[]),
        assetDeclaredAvailabilities: assetEntries.map((entry) => ({
          ...mapToCommonAvailabilityFields(entry, chartEnd),
          assetName: entry.assetName,
        })),
        timeHorizon,
      };
      return getAssetLevelAvailabilityChartEntries(chartInput);
    }
  })();

  // Get the preview range from all preview entries
  const previewRange = getPreviewEntriesRange(previewEntries);

  return getAvailabilityChartData(
    chartEntries,
    availabilityFormOpen,
    previewRange,
  );
}

export const getPreviewAvailabilitySeries = (
  chartEntriesResult: Result<
    INetAvailabilityError[] | IntervalDoesNotAlignToSpError,
    AvailabilityChartEntry[]
  >,
  parameter: 'maxImportPower' | 'maxExportPower' | 'energyCapacity',
  newEntryStart: string,
  newEntryEnd: string | null,
): AvailabilityChartSeries => {
  // Truncates the availability profile to only dates that are within the new entry range
  const newSeries: [number, number][] = [];

  if (!chartEntriesResult.success) {
    return newSeries;
  }

  if (chartEntriesResult.value.length === 0) {
    return newSeries;
  }

  // keep track of the value before the new entry so we can have the vertical line (step line chart)
  let firstNewEntryChartValue = chartEntriesResult.value[0][parameter];
  let shouldSetFirstEntryValue = true;

  for (let i = 0; i < chartEntriesResult.value.length; i++) {
    const entry = chartEntriesResult.value[i];

    if (
      entry.date >= new Date(newEntryStart) &&
      (newEntryEnd === null || entry.date <= new Date(newEntryEnd))
    ) {
      newSeries.push([entry.date.getTime(), entry[parameter]]);
      shouldSetFirstEntryValue = false;
    }

    if (shouldSetFirstEntryValue) {
      firstNewEntryChartValue = entry[parameter];
    }
  }

  // Add the first entry value to get the vertical line
  newSeries.unshift([
    new Date(newEntryStart).getTime(),
    firstNewEntryChartValue,
  ]);

  return newSeries;
};

export const getAvailabilityChartData = (
  chartEntriesResult: Result<
    INetAvailabilityError[] | IntervalDoesNotAlignToSpError,
    AvailabilityChartEntry[]
  >,
  isPreviewMode: boolean,
  previewRange: { start: string; end: string | null } | null,
): AvailabilityChartData => {
  if (!chartEntriesResult.success) {
    return {
      power: [],
      energy: [],
    };
  }

  const hasPreview = isPreviewMode && previewRange !== null;

  return {
    power: [
      {
        id: 'export',
        name: 'Export Power Limit',
        data: chartEntriesResult.value.map((entry) => {
          return [entry.date.getTime(), entry.maxExportPower];
        }),
        stack: 'positive',
        color: powerColors.color,
        lineColor: powerColors.lineColor,
        ...baseConfig,
      },
      {
        id: 'import',
        name: 'Import Power Limit',
        data: chartEntriesResult.value.map((entry) => {
          return [entry.date.getTime(), entry.maxImportPower];
        }),
        stack: 'negative',
        color: powerColors.color,
        lineColor: powerColors.lineColor,
        ...baseConfig,
      },
      {
        id: 'new-export',
        name: 'New Export Power Limit',
        data: hasPreview
          ? getPreviewAvailabilitySeries(
              chartEntriesResult,
              'maxExportPower',
              previewRange.start,
              previewRange.end,
            )
          : [],
        stack: 'reduction',
        color: previewColors.color,
        lineColor: previewColors.lineColor,
        showInLegend: hasPreview,
        ...previewConfig,
      },
      {
        id: 'new-import',
        name: 'New Import Power Limit',
        data: hasPreview
          ? getPreviewAvailabilitySeries(
              chartEntriesResult,
              'maxImportPower',
              previewRange.start,
              previewRange.end,
            )
          : [],
        stack: 'reduction',
        color: previewColors.color,
        lineColor: previewColors.lineColor,
        showInLegend: hasPreview,
        ...previewConfig,
      },
    ],
    energy: [
      {
        id: 'energyCapacity',
        name: 'Energy Capacity',
        color: powerColors.color,
        lineColor: powerColors.lineColor,
        data: chartEntriesResult.value.map((entry) => {
          return [entry.date.getTime(), entry.energyCapacity];
        }),
        ...baseConfig,
      },
      {
        id: 'new-energy',
        name: 'New Energy Capacity',
        data: hasPreview
          ? getPreviewAvailabilitySeries(
              chartEntriesResult,
              'energyCapacity',
              previewRange.start,
              previewRange.end,
            )
          : [],
        stack: 'reduction',
        color: previewColors.color,
        lineColor: previewColors.lineColor,
        showInLegend: hasPreview,
        ...previewConfig,
      },
    ],
  };
};
